import { useEffect, useRef, useState } from "react";
import { UserType } from "@/modules/agents/dtos/index.v2";
import { useTheme } from "@mui/material";

interface targetsData {
  period: string;
  current: string;
  target: string;
  percentage: number;
  effectiveTargetType?: string;
  targetType?: "team" | "personal";
  userType?: UserType;
}

interface UseTargetCardProps {
  targetsData?: targetsData[];
}

interface UseTargetCardReturn {
  cardRef: React.RefObject<HTMLDivElement>;
  cardWidth: number;
  progressBarWidth: number;
  hasTeamTarget: boolean;
  hasPersonalTarget: boolean;
  allPeriods: string[];
  createPlaceholderData: (
    period: string,
    targetType: "team" | "personal"
  ) => targetsData;
  getRowsToShow: (period: string) => targetsData[];
  getUserTypeChipColors: (userType?: UserType | string) => {
    backgroundColor: string | undefined;
    textColor: string | undefined;
  };
}

export const useTargetCard = ({
  targetsData,
}: UseTargetCardProps): UseTargetCardReturn => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardWidth, setCardWidth] = useState(0);
  const [progressBarWidth, setProgressBarWidth] = useState(0);
  const theme = useTheme();

  // Determine which targetTypes of targets exist in the provided data set
  const hasTeamTarget = targetsData?.some(
    (d) => d.targetType === "team" && parseFloat(d.target.replace(/,/g, "")) > 0
  );
  const hasPersonalTarget = targetsData?.some(
    (d) =>
      d.targetType === "personal" &&
      (parseFloat(d.target.replace(/,/g, "")) > 0 ||
        parseFloat(d.current.replace(/,/g, "")) > 0)
  );

  // Update card width on window resize
  useEffect(() => {
    const updateDimensions = () => {
      const newCardWidth = window.innerWidth - 300;
      setCardWidth(newCardWidth);

      // Calculate progress bar width based on card width
      // Account for: name section (180px), icon button (48px), gaps, and padding
      const availableWidth = newCardWidth - 180 - 48 - 100; // 100px for gaps and padding
      const calculatedWidth = Math.max(250, Math.floor(availableWidth / 4));
      setProgressBarWidth(calculatedWidth);
    };

    // Initial calculation
    updateDimensions();

    // Add resize listener
    window.addEventListener("resize", updateDimensions);

    // Cleanup
    return () => {
      window.removeEventListener("resize", updateDimensions);
    };
  }, []);

  // Always show all expected periods
  const allPeriods = ["M1", "M2", "M3", "Total"];

  // Helper function to create placeholder data for missing periods
  const createPlaceholderData = (
    period: string,
    targetType: "team" | "personal"
  ): targetsData => ({
    period,
    current: "0",
    target: "0",
    percentage: 0,
    targetType,
  });

  // Function to get rows to show for a specific period
  const getRowsToShow = (period: string): targetsData[] => {
    // Find existing data for this period
    const teamRow = targetsData?.find(
      (d) => d.period === period && (d.targetType ?? "team") === "team"
    );
    const personalRow = targetsData?.find(
      (d) => d.period === period && d.targetType === "personal"
    );

    // Determine what to show based on user's target types
    const shouldShowTeam = hasTeamTarget;
    const shouldShowPersonal = hasPersonalTarget;

    // Create rows to display
    const rowsToShow: targetsData[] = [];

    if (shouldShowTeam) {
      rowsToShow.push(teamRow || createPlaceholderData(period, "team"));
    }

    if (shouldShowPersonal) {
      rowsToShow.push(personalRow || createPlaceholderData(period, "personal"));
    }

    // If no target types detected, show team row as fallback
    if (!shouldShowTeam && !shouldShowPersonal) {
      rowsToShow.push(teamRow || createPlaceholderData(period, "team"));
    }

    return rowsToShow;
  };

  // Map to get chip colors based on user type
  const getUserTypeChipColors = (userType?: UserType | string) => {
    switch (userType) {
      case UserType.MOVER:
        return {
          backgroundColor: theme.nawy.colors.primitives.yellow[100],
          textColor: theme.nawy.colors.primitives.yellow[900],
        };
      case UserType.LEAVER:
        return {
          backgroundColor: theme.nawy.colors.primitives.red[100],
          textColor: theme.nawy.colors.primitives.red[900],
        };
      case UserType.NORMAL:
      case UserType.JOINER:
      default:
        return {
          backgroundColor: theme.nawy.colors.primitives.green[100],
          textColor: theme.nawy.colors.primitives.green[900],
        };
    }
  };

  return {
    cardRef,
    cardWidth,
    progressBarWidth,
    hasTeamTarget: hasTeamTarget || false,
    hasPersonalTarget: hasPersonalTarget || false,
    allPeriods,
    createPlaceholderData,
    getRowsToShow,
    getUserTypeChipColors,
  };
};
